import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { seoArticles } from '@/mocks/seoArticles';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/base/JsonLd';
import { getOrganizationSchema, getWebSiteSchema, getWebPageSchema, getBreadcrumbSchema } from '@/lib/schema';

const testimonials = [
  {
    name: "Marco R.",
    role: "Acquirente abituale",
    text: "Itaobuy Spreadsheet ha completamente trasformato il mio modo di fare shopping. Trovo sempre i prodotti migliori a prezzi imbattibili. La guida è chiara e completa.",
    rating: 5,
  },
  {
    name: "Giulia B.",
    role: "Appassionata di moda",
    text: "Finalmente una risorsa italiana seria per lo shopping internazionale. Il database è sempre aggiornato e le recensioni mi hanno aiutata a evitare brutte sorprese.",
    rating: 5,
  },
  {
    name: "Alessandro F.",
    role: "Collezionista sneakers",
    text: "Come collezionista di sneakers, Itaobuy Spreadsheet è diventato il mio punto di riferimento. Le categorie sono ben organizzate e i prezzi sono competitivi.",
    rating: 5,
  },
  {
    name: "Francesca M.",
    role: "Shopping enthusiast",
    text: "La sezione sulla sicurezza è fondamentale. Grazie alle guide ho imparato a riconoscere i venditori affidabili e a fare acquisti in totale tranquillità.",
    rating: 4,
  },
  {
    name: "Lorenzo T.",
    role: "Studente universitario",
    text: "Con un budget limitato, Itaobuy Spreadsheet mi permette di trovare capi di qualità senza spendere una fortuna. Consigliatissimo a tutti gli studenti!",
    rating: 5,
  },
  {
    name: "Valentina S.",
    role: "Imprenditrice",
    text: "Utilizzo Itaobuy Spreadsheet da mesi e non potrei più farne a meno. L'organizzazione delle categorie e la velocità degli aggiornamenti sono impressionanti.",
    rating: 5,
  },
];

const categoryImages: Record<string, string> = {
  "Shoes": "https://readdy.ai/api/search-image?query=Single%20white%20and%20cream%20sneaker%20standing%20on%20soft%20beige%20linen%20surface%2C%20clean%20minimal%20product%20photography%2C%20diffused%20natural%20window%20light%2C%20no%20shadows%2C%20elegant%20editorial%20style%2C%20isolated%20product%20shot%20with%20subtle%20texture%20background&width=120&height=120&seq=cat-img-shoes&orientation=squarish",
  "Hoodies/Sweaters": "https://readdy.ai/api/search-image?query=Single%20oatmeal%20colored%20hoodie%20folded%20neatly%20on%20cream%20textured%20fabric%2C%20soft%20warm%20natural%20light%2C%20minimal%20product%20photography%2C%20clean%20aesthetic%2C%20no%20visible%20background%20distractions%2C%20studio%20quality%20isolated%20shot&width=120&height=120&seq=cat-img-hoodies&orientation=squarish",
  "T-Shirts": "https://readdy.ai/api/search-image?query=Single%20white%20cotton%20t-shirt%20neatly%20folded%20on%20warm%20beige%20linen%20background%2C%20minimal%20product%20photography%2C%20soft%20diffused%20daylight%2C%20clean%20studio%20aesthetic%2C%20simple%20and%20elegant%20isolated%20product%20shot&width=120&height=120&seq=cat-img-tshirts&orientation=squarish",
  "Jackets": "https://readdy.ai/api/search-image?query=Single%20olive%20green%20bomber%20jacket%20laid%20flat%20on%20cream%20textured%20surface%2C%20warm%20natural%20light%2C%20minimal%20product%20photography%2C%20clean%20editorial%20style%2C%20soft%20shadows%2C%20isolated%20fashion%20product%20shot&width=120&height=120&seq=cat-img-jackets&orientation=squarish",
  "Pants/Shorts": "https://readdy.ai/api/search-image?query=Single%20pair%20of%20beige%20chino%20pants%20folded%20neatly%20on%20cream%20linen%20fabric%2C%20soft%20warm%20daylight%2C%20minimal%20product%20photography%2C%20clean%20aesthetic%2C%20isolated%20clothing%20shot%20with%20gentle%20texture&width=120&height=120&seq=cat-img-pants&orientation=squarish",
  "Headwear": "https://readdy.ai/api/search-image?query=Single%20cream%20colored%20baseball%20cap%20on%20warm%20beige%20textured%20surface%2C%20diffused%20natural%20light%2C%20minimal%20product%20photography%2C%20clean%20studio%20aesthetic%2C%20elegant%20isolated%20accessory%20shot&width=120&height=120&seq=cat-img-headwear&orientation=squarish",
  "Sets": "https://readdy.ai/api/search-image?query=Matching%20cream%20tracksuit%20set%20neatly%20arranged%20on%20soft%20beige%20linen%2C%20warm%20diffused%20daylight%2C%20minimal%20product%20photography%2C%20clean%20editorial%20style%2C%20coordinated%20outfit%20flat%20lay&width=120&height=120&seq=cat-img-sets&orientation=squarish",
  "Underwear/Underpants": "https://readdy.ai/api/search-image?query=Soft%20cream%20colored%20cotton%20underwear%20neatly%20folded%20on%20warm%20beige%20textured%20fabric%2C%20diffused%20natural%20light%2C%20minimal%20and%20tasteful%20product%20photography%2C%20clean%20studio%20aesthetic&width=120&height=120&seq=cat-img-underwear&orientation=squarish",
  "Jersey": "https://readdy.ai/api/search-image?query=Single%20red%20and%20white%20striped%20football%20jersey%20folded%20on%20cream%20linen%20surface%2C%20warm%20natural%20light%2C%20minimal%20sports%20product%20photography%2C%20clean%20editorial%20style%2C%20elegant%20athletic%20aesthetic&width=120&height=120&seq=cat-img-jersey&orientation=squarish",
  "Accessories": "https://readdy.ai/api/search-image?query=Single%20pair%20of%20tortoiseshell%20sunglasses%20on%20warm%20beige%20textured%20surface%2C%20soft%20diffused%20daylight%2C%20minimal%20product%20photography%2C%20clean%20elegant%20studio%20shot%2C%20sophisticated%20accessory&width=120&height=120&seq=cat-img-accessories&orientation=squarish",
  "Bags": "https://readdy.ai/api/search-image?query=Single%20cognac%20brown%20leather%20tote%20bag%20on%20cream%20linen%20background%2C%20warm%20natural%20light%2C%20minimal%20product%20photography%2C%20clean%20editorial%20style%2C%20elegant%20isolated%20accessory%20shot%20with%20subtle%20textures&width=120&height=120&seq=cat-img-bags&orientation=squarish",
  "Belts": "https://readdy.ai/api/search-image?query=Single%20dark%20brown%20leather%20belt%20coiled%20elegantly%20on%20warm%20beige%20textured%20surface%2C%20soft%20diffused%20daylight%2C%20minimal%20product%20photography%2C%20clean%20studio%20aesthetic%2C%20sophisticated%20accessory&width=120&height=120&seq=cat-img-belts&orientation=squarish",
  "Perfumes": "https://readdy.ai/api/search-image?query=Single%20amber%20glass%20perfume%20bottle%20on%20cream%20linen%20background%2C%20warm%20natural%20light%2C%20minimal%20product%20photography%2C%20clean%20elegant%20aesthetic%2C%20soft%20reflections%2C%20sophisticated%20fragrance%20shot&width=120&height=120&seq=cat-img-perfumes&orientation=squarish",
  "Other Stuff": "https://readdy.ai/api/search-image?query=Eclectic%20collection%20of%20small%20lifestyle%20items%20including%20a%20notebook%2C%20ceramic%20mug%20and%20keychain%20on%20warm%20beige%20textured%20surface%2C%20soft%20diffused%20daylight%2C%20minimal%20product%20photography%2C%20curated%20aesthetic&width=120&height=120&seq=cat-img-other&orientation=squarish",
};

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <span key={star} className="w-4 h-4 flex items-center justify-center">
          <i className={`text-sm ${star <= rating ? 'ri-star-fill text-amber-400' : 'ri-star-line text-foreground-300'}`}></i>
        </span>
      ))}
    </div>
  );
}

export default function Home() {
  const { t } = useTranslation();
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd schema={getOrganizationSchema()} />
      <JsonLd schema={getWebSiteSchema()} />
      <JsonLd schema={getWebPageSchema('/', 'Itaobuy Spreadsheet Italia - Home', 'Database prodotti aggiornato per lo shopping internazionale. Categorie scarpe, abbigliamento, accessori con prezzi e valutazioni verificate. Guide esperte in italiano per acquisti sicuri.')} />
      <JsonLd schema={getBreadcrumbSchema([{ name: 'Home', url: '/' }])} />
      <Navbar />

      {/* ===== HERO SECTION ===== */}
      <section className="relative w-full min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Elegant%20product%20photography%20flat%20lay%20composition%20with%20white%20leather%20sneakers%2C%20cream%20cashmere%20sweater%2C%20tan%20leather%20bag%2C%20gold%20toned%20accessories%2C%20and%20dried%20pampas%20grass%20on%20a%20textured%20linen%20surface%2C%20warm%20golden%20hour%20sunlight%20streaming%20from%20left%2C%20sophisticated%20european%20minimalism%20aesthetic%2C%20soft%20organic%20shadows%2C%20luxury%20editorial%20style%2C%20harmonious%20neutral%20palette&width=1600&height=900&seq=itaobuy-hero-bg-v2&orientation=landscape"
            alt="Itaobuy Spreadsheet prodotti"
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/15 to-black/35"></div>

        <div className="relative z-10 w-full px-6 md:px-10 py-32 md:py-40">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
            <div className="flex-1 text-center lg:text-left">
              <h1 className="font-heading text-5xl md:text-7xl lg:text-8xl font-light text-background-50 leading-tight tracking-tight">
                {t('hero.title1')}
                <br />
                <span className="font-semibold italic">{t('hero.title2')}</span>
              </h1>
              <p className="mt-6 md:mt-8 text-background-50/80 font-body text-base md:text-lg leading-relaxed max-w-xl mx-auto lg:mx-0">
                {t('hero.subtitle')}
              </p>
              <div className="flex flex-col sm:flex-row items-center gap-4 mt-8 md:mt-10 justify-center lg:justify-start">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="group flex items-center gap-3 px-8 py-3.5 bg-primary-500 text-background-50 font-label text-sm font-semibold uppercase tracking-wider rounded-lg hover:bg-primary-600 transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  {t('hero.cta1')}
                  <span className="w-5 h-5 flex items-center justify-center border border-background-50/40 rounded-full group-hover:translate-x-0.5 transition-transform">
                    <i className="ri-arrow-right-line text-xs"></i>
                  </span>
                </a>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="group flex items-center gap-3 px-8 py-3.5 border-2 border-background-50/60 text-background-50 font-label text-sm font-semibold uppercase tracking-wider rounded-lg hover:bg-background-50/10 transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  {t('hero.cta2')}
                  <span className="w-5 h-5 flex items-center justify-center border border-background-50/40 rounded-full group-hover:translate-x-0.5 transition-transform">
                    <i className="ri-arrow-right-line text-xs"></i>
                  </span>
                </a>
              </div>
            </div>

            <div className="flex-1 hidden lg:flex justify-center">
              <div className="bg-background-50/10 backdrop-blur-sm border border-background-50/20 rounded-2xl p-6 w-full max-w-md">
                <div className="flex items-center justify-between mb-5">
                  <span className="font-label text-xs font-semibold text-background-50/80 uppercase tracking-wider whitespace-nowrap">Itaobuy Spreadsheet Database</span>
                  <span className="w-2 h-2 rounded-full bg-accent-400 animate-pulse"></span>
                </div>
                <div className="space-y-3">
                  {[
                    { name: "Jordan 4 Retro", cat: "Sneakers", price: "¥480", quality: "9.2" },
                    { name: "Nike Dunk Low", cat: "Sneakers", price: "¥320", quality: "8.8" },
                    { name: "Essentials Hoodie", cat: "Hoodies", price: "¥180", quality: "9.0" },
                    { name: "Gallery Dept. Tee", cat: "T-Shirts", price: "¥120", quality: "8.5" },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3 bg-background-50/5 rounded-lg px-4 py-3">
                      <div className="w-10 h-10 rounded-lg bg-background-50/10 flex items-center justify-center flex-shrink-0">
                        <i className="ri-shopping-bag-line text-background-50/70"></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-label text-xs font-medium text-background-50 truncate whitespace-nowrap">{item.name}</p>
                        <p className="font-body text-[10px] text-background-50/50 whitespace-nowrap">{item.cat}</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="font-label text-xs font-semibold text-background-50 whitespace-nowrap">{item.price}</p>
                        <p className="font-body text-[10px] text-accent-400 whitespace-nowrap">★ {item.quality}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-5 pt-4 border-t border-background-50/10">
                  <p className="font-body text-[10px] text-background-50/40 text-center whitespace-nowrap">
                    Database aggiornato recentemente • {new Date().toLocaleDateString('it-IT')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== TRUST BAR ===== */}
      <section className="bg-background-100 border-y border-background-200">
        <div className="w-full px-6 md:px-10 py-5">
          <div className="flex flex-wrap items-center justify-center gap-6 md:gap-12">
            {[
              { text: t('trust.item1'), icon: 'ri-refresh-line' },
              { text: t('trust.item2'), icon: 'ri-global-line' },
              { text: t('trust.item3'), icon: 'ri-file-search-line' },
              { text: t('trust.item4'), icon: 'ri-shield-check-line' },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className={`${item.icon} text-accent-500 text-sm`}></i>
                </span>
                <span className="font-body text-xs md:text-sm text-foreground-700 whitespace-nowrap">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CATEGORY GRID WITH IMAGES ===== */}
      <section className="w-full px-6 md:px-10 py-16 md:py-24">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <span className="inline-block px-4 py-1.5 rounded-full border border-foreground-200 text-foreground-600 font-label text-xs uppercase tracking-wider whitespace-nowrap">
              {t('categories.label')}
            </span>
            <h2 className="mt-5 font-heading text-3xl md:text-5xl font-bold text-foreground-950 leading-tight">
              {t('categories.title1')}
              <br />
              {t('categories.title2')}
            </h2>
            <p className="mt-4 text-foreground-600 font-body text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
              {t('categories.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3 md:gap-4">
            {categories.map((cat) => (
              <a
                key={cat.name}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                onMouseEnter={() => setActiveCategory(cat.name)}
                onMouseLeave={() => setActiveCategory(null)}
                className={`group flex flex-col items-center text-center p-3 md:p-4 rounded-xl border border-background-200 transition-all duration-300 cursor-pointer ${
                  activeCategory === cat.name
                    ? 'bg-accent-500 border-accent-500'
                    : 'bg-background-50 hover:border-accent-300'
                }`}
              >
                <div className="w-full aspect-square rounded-lg overflow-hidden mb-3 bg-background-100">
                  <img
                    src={categoryImages[cat.name] || categoryImages["Shoes"]}
                    alt={cat.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <span className={`font-label text-xs md:text-sm font-semibold transition-colors duration-300 whitespace-nowrap ${
                  activeCategory === cat.name ? 'text-background-50' : 'text-foreground-800'
                }`}>
                  {cat.name}
                </span>
                <span className={`mt-1 font-body text-[10px] md:text-xs leading-relaxed transition-colors duration-300 line-clamp-2 ${
                  activeCategory === cat.name ? 'text-background-50/80' : 'text-foreground-500'
                }`}>
                  {cat.desc}
                </span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* ===== GUIDE & DATABASE SECTION ===== */}
      <section className="w-full px-6 md:px-10 py-16 md:py-24 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-16 mb-12">
            <div className="lg:w-1/3">
              <h2 className="font-heading text-3xl md:text-5xl font-bold text-foreground-950 leading-tight">
                {t('guide.title')}
                <br />
                <span className="text-accent-500">{t('guide.subtitle')}</span>
              </h2>
            </div>
            <div className="lg:w-2/3 flex items-end">
              <p className="font-body text-sm md:text-base text-foreground-600 leading-relaxed">
                {t('guide.desc')}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Link to="/itaobuy-spreadsheet" className="group relative md:col-span-2 rounded-xl overflow-hidden bg-primary-700 min-h-[320px] flex flex-col justify-end p-6 md:p-8 cursor-pointer">
              <div className="absolute inset-0">
                <img
                  src="https://readdy.ai/api/search-image?query=Warm%20cozy%20workspace%20with%20open%20MacBook%20displaying%20colorful%20spreadsheet%20dashboard%20with%20product%20data%20charts%2C%20ceramic%20matcha%20latte%20cup%2C%20cream%20knitted%20throw%20blanket%2C%20dried%20eucalyptus%20in%20amber%20vase%2C%20soft%20golden%20evening%20glow%20through%20sheer%20curtains%2C%20dark%20oak%20desk%2C%20sophisticated%20editorial%20lifestyle%20photography%2C%20cream%20and%20terracotta%20tones&width=800&height=500&seq=guide-card-main-v2&orientation=landscape"
                  alt="Guida Completa Itaobuy"
                  className="w-full h-full object-cover opacity-60 group-hover:opacity-70 transition-opacity"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary-900/90 to-transparent"></div>
              </div>
              <div className="relative z-10">
                <span className="inline-block px-3 py-1 rounded-full bg-accent-500 text-background-50 font-label text-xs font-semibold uppercase tracking-wider whitespace-nowrap mb-3">Guida SEO</span>
                <h3 className="font-heading text-xl md:text-2xl font-semibold text-background-50 leading-snug">{t('guide.card1.title')}</h3>
                <p className="mt-2 text-background-50/70 font-body text-sm leading-relaxed max-w-lg">{t('guide.card1.desc')}</p>
                <span className="inline-flex items-center gap-2 mt-4 px-5 py-2.5 bg-background-50 text-primary-800 font-label text-xs font-semibold uppercase tracking-wider rounded-full group-hover:bg-accent-500 group-hover:text-background-50 transition-colors cursor-pointer whitespace-nowrap">
                  {t('guide.card1.btn')}
                  <i className="ri-arrow-right-line text-sm"></i>
                </span>
              </div>
            </Link>

            <Link to="/database" className="group flex flex-col rounded-xl border border-background-200 bg-background-50 overflow-hidden cursor-pointer hover:border-accent-300 transition-colors">
              <div className="h-40 overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Organized%20product%20catalog%20grid%20display%20with%20sneakers%2C%20leather%20goods%20and%20accessories%20arranged%20on%20minimalist%20open%20shelving%2C%20warm%20cream%20walls%20background%2C%20soft%20natural%20light%20from%20skylight%2C%20boutique%20retail%20aesthetic%2C%20clean%20editorial%20catalog%20photography%2C%20sophisticated%20neutral%20palette&width=600&height=300&seq=guide-card-db-v2&orientation=landscape"
                  alt="Database Prodotti"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="flex-1 p-5 flex flex-col justify-between">
                <div>
                  <span className="inline-block px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-800 font-label text-[10px] font-semibold uppercase tracking-wider whitespace-nowrap mb-2">Database</span>
                  <h3 className="font-label text-base font-semibold text-foreground-950 leading-snug">{t('guide.card2.title')}</h3>
                  <p className="mt-1.5 text-foreground-500 font-body text-xs leading-relaxed">{t('guide.card2.desc')}</p>
                </div>
                <span className="inline-flex items-center gap-1.5 mt-4 text-accent-600 font-label text-xs font-semibold group-hover:text-accent-500 transition-colors cursor-pointer whitespace-nowrap">
                  {t('guide.card2.btn')}
                  <i className="ri-arrow-right-line text-xs"></i>
                </span>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* ===== SEO ARTICLES GRID (9 articles) ===== */}
      <section className="w-full px-6 md:px-10 py-16 md:py-24 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <span className="inline-block px-4 py-1.5 rounded-full border border-foreground-200 text-foreground-600 font-label text-xs uppercase tracking-wider whitespace-nowrap">
              Guide & Articoli SEO
            </span>
            <h2 className="mt-5 font-heading text-3xl md:text-5xl font-bold text-foreground-950 leading-tight">
              Risorse Complete su
              <br />
              Itaobuy Spreadsheet
            </h2>
            <p className="mt-4 text-foreground-600 font-body text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
              Esplora la nostra raccolta di guide dettagliate, analisi e tutorial per padroneggiare ogni aspetto dello shopping internazionale con Itaobuy Spreadsheet.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {seoArticles.map((article) => (
              <Link
                key={article.id}
                to={article.slug}
                className="group flex flex-col bg-background-50 border border-background-200 rounded-xl overflow-hidden hover:border-accent-300 transition-all duration-300 cursor-pointer"
              >
                <div className="h-44 overflow-hidden">
                  <img
                    src={article.image}
                    alt={article.imageAlt}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="flex-1 p-4 md:p-5 flex flex-col">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-time-line text-foreground-400 text-xs"></i>
                    </span>
                    <span className="font-body text-[10px] text-foreground-500">{article.readTime}</span>
                  </div>
                  <h3 className="font-label text-sm md:text-base font-semibold text-foreground-900 leading-snug group-hover:text-primary-500 transition-colors line-clamp-2">
                    {article.title}
                  </h3>
                  <p className="mt-2 font-body text-xs text-foreground-500 leading-relaxed line-clamp-2">
                    {article.excerpt}
                  </p>
                  <div className="mt-auto pt-4 flex items-center gap-1.5">
                    <span className="font-label text-xs font-semibold text-accent-600 group-hover:text-accent-500 transition-colors whitespace-nowrap">
                      Leggi l&apos;articolo
                    </span>
                    <span className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-arrow-right-line text-accent-600 text-xs group-hover:translate-x-0.5 transition-transform"></i>
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CTA SECTION ===== */}
      <section className="w-full px-6 md:px-10 py-16 md:py-24 bg-background-100">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-3xl md:text-5xl font-bold text-foreground-950 leading-tight">
            {t('cta.title')}
          </h2>
          <p className="mt-4 text-foreground-600 font-body text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
            {t('cta.subtitle')}
          </p>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-3 mt-8 px-8 py-4 bg-primary-500 text-background-50 font-label text-sm font-semibold uppercase tracking-wider rounded-full hover:bg-primary-600 transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            {t('cta.btn')}
            <span className="w-6 h-6 flex items-center justify-center">
              <i className="ri-arrow-right-line text-sm"></i>
            </span>
          </a>

          <div className="mt-14 flex items-center justify-center gap-4 md:gap-8 flex-wrap">
            {[
              "https://readdy.ai/api/search-image?query=Single%20beige%20suede%20loafer%20isolated%20on%20clean%20cream%20background%2C%20premium%20product%20photography%2C%20soft%20diffused%20lighting%2C%20minimal%20shadows%2C%20elegant%20neutral%20aesthetic%2C%20studio%20quality%20isolated%20product%20shot&width=200&height=200&seq=strip-shoe-v2&orientation=squarish",
              "https://readdy.ai/api/search-image?query=Single%20sage%20green%20knit%20sweater%20folded%20neatly%20on%20cream%20textured%20surface%2C%20soft%20natural%20light%2C%20minimal%20product%20photography%2C%20clean%20aesthetic%2C%20isolated%20clothing%20shot%20with%20organic%20texture&width=200&height=200&seq=strip-hoodie-v2&orientation=squarish",
              "https://readdy.ai/api/search-image?query=Single%20taupe%20leather%20crossbody%20bag%20with%20gold%20hardware%20on%20cream%20linen%20background%2C%20soft%20warm%20light%2C%20minimal%20product%20photography%2C%20elegant%20clean%20aesthetic%2C%20studio%20quality%20accessory%20shot&width=200&height=200&seq=strip-bag-v2&orientation=squarish",
              "https://readdy.ai/api/search-image?query=Single%20sand%20colored%20bucket%20hat%20on%20warm%20beige%20textured%20surface%2C%20soft%20diffused%20light%2C%20minimal%20product%20photography%2C%20clean%20casual%20aesthetic%2C%20isolated%20accessory%20shot&width=200&height=200&seq=strip-cap-v2&orientation=squarish",
              "https://readdy.ai/api/search-image?query=Single%20minimalist%20watch%20with%20tan%20leather%20strap%20on%20cream%20linen%20background%2C%20soft%20natural%20light%2C%20elegant%20product%20photography%2C%20refined%20clean%20aesthetic%2C%20isolated%20timepiece%20shot%20with%20subtle%20shadows&width=200&height=200&seq=strip-watch-v2&orientation=squarish",
            ].map((img, i) => (
              <div key={i} className="w-24 h-24 md:w-28 md:h-28 rounded-xl overflow-hidden bg-background-200">
                <img src={img} alt="Prodotto Itaobuy" className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== COMMUNITY SECTION ===== */}
      <section className="w-full px-6 md:px-10 py-16 md:py-24 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <div className="flex items-center justify-center gap-2 mb-4">
              <span className="w-2 h-2 rounded-full bg-accent-500"></span>
              <span className="font-label text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">{t('community.label')}</span>
            </div>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-foreground-950 leading-tight">
              {t('community.title1')}
            </h2>
            <p className="mt-4 text-foreground-600 font-body text-sm max-w-xl mx-auto leading-relaxed">
              {t('community.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {testimonials.map((item, i) => (
              <div
                key={i}
                className={`bg-background-100 rounded-xl p-5 md:p-6 border border-background-200 ${
                  i === 5 ? 'opacity-50' : ''
                }`}
              >
                <StarRating rating={item.rating} />
                <h4 className="mt-3 font-label text-sm font-semibold text-foreground-900 leading-snug">
                  &quot;{item.text.length > 80 ? item.text.slice(0, 80) + '...' : item.text}&quot;
                </h4>
                <p className="mt-2 text-foreground-500 font-body text-xs leading-relaxed">{item.text}</p>
                <div className="flex items-center gap-3 mt-4 pt-4 border-t border-background-200">
                  <div className="w-9 h-9 rounded-full bg-primary-200 flex items-center justify-center flex-shrink-0">
                    <span className="font-label text-xs font-semibold text-primary-700">
                      {item.name.split(' ')[0][0]}{item.name.split(' ')[1]?.[0] || ''}
                    </span>
                  </div>
                  <div>
                    <p className="font-label text-xs font-semibold text-foreground-800 whitespace-nowrap">{item.name}</p>
                    <p className="font-body text-[10px] text-foreground-500 whitespace-nowrap">{item.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== ABOUT SECTION ===== */}
      <section className="w-full px-6 md:px-10 py-16 md:py-24 bg-background-100">
        <div className="max-w-3xl mx-auto text-center">
          <span className="inline-block px-4 py-1.5 rounded-full border border-foreground-200 text-foreground-600 font-label text-xs uppercase tracking-wider whitespace-nowrap mb-6">
            {t('about.label')}
          </span>
          <h2 className="font-heading text-3xl md:text-5xl lg:text-6xl font-light text-foreground-950 leading-tight">
            {t('about.title1')}
            <br />
            <span className="inline-flex items-center gap-3">
              {t('about.title2')}
              <span className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-accent-100 flex items-center justify-center">
                <i className="ri-shopping-bag-3-line text-xl md:text-2xl text-accent-600"></i>
              </span>
            </span>
            <br />
            {t('about.title3')}
          </h2>
          <p className="mt-8 text-foreground-600 font-body text-sm md:text-base leading-relaxed max-w-xl mx-auto">
            {t('about.desc')}
          </p>
          <Link
            to="/itaobuy-spreadsheet"
            className="inline-flex items-center gap-2 mt-8 px-6 py-3 border border-primary-300 text-primary-600 font-label text-sm font-semibold rounded-full hover:bg-primary-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            Scopri di più
            <i className="ri-arrow-right-line text-sm"></i>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}